// src/components/Dashboard.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LayoutDashboard,
  FileText,
  CheckCircle2,
  ArrowLeftRight,
  Megaphone,
  AlertCircle,
  Clock,
  ChevronRight,
  Bell,
  Trophy,
  Activity,
  MapPin,
  Vote,
  FileEdit,
  Check,
  Calendar,
  Shuffle,
  TableProperties,
  CreditCard
} from 'lucide-react';
import { getAnnouncements, getCurrentAssignments, getPaymentStatus, getSchedules, getUserInfo, syncUserInfo } from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import '../styles/Dashboard.css';

function Dashboard() {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [isEmailConfirmedInDb, setIsEmailConfirmedInDb] = useState(true);
  const [syncingEmailStatus, setSyncingEmailStatus] = useState(false);
  const [syncEmailStatusError, setSyncEmailStatusError] = useState('');
  const [applicationStatus, setApplicationStatus] = useState({
    ruralChoice: false,
    firstChoice: false,
    secondChoice: false
  });
  const [paymentStatus, setPaymentStatus] = useState({ isPaid: false, status: 'unknown' });
  const [results, setResults] = useState({
    firstResult: null,
    secondResult: null,
    finalResult: null
  });
  const [exchangeStatus, setExchangeStatus] = useState({
    hospital: false,
    rural: false
  });
  const [schedules, setSchedules] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);

      // 並行してデータを取得
      const [schedulesData, userInfo, announcements, currentAssignments, paymentStatusData] = await Promise.all([
        getSchedules().catch(e => {
          // セキュリティ: エラーの詳細をログに出力しない
          return [];
        }),
        getUserInfo().catch(e => {
          // セキュリティ: エラーの詳細をログに出力しない
          return null;
        }),
        getAnnouncements().catch(() => []),
        getCurrentAssignments().catch(() => []),
        getPaymentStatus().catch(() => null)
      ]);

      setSchedules(schedulesData);
      setNotifications(announcements || []);

      if (userInfo) {
        setIsEmailConfirmedInDb(Boolean(userInfo.is_email_confirmed));
        const hasNonRuralAssignment = Array.isArray(currentAssignments) && currentAssignments.some((assignment) => {
          const stage = assignment?.assignment_stage;
          return stage !== undefined && stage !== null && stage !== 'rural' && stage !== 4 && stage !== '4';
        });

        setApplicationStatus({
          ruralChoice: userInfo.rural_preference_status,
          firstChoice: userInfo.first_preference_status,
          secondChoice: userInfo.second_preference_status
        });

        setResults({
          firstResult: hasNonRuralAssignment ? true : null,
          secondResult: userInfo.second_result_status,
          finalResult: userInfo.final_result_status
        });

        setExchangeStatus({
          hospital: userInfo.hospital_exchange_preference_status,
          rural: userInfo.rural_exchange_preference_status
        });
      }

      if (paymentStatusData) {
        setPaymentStatus({
          isPaid: Boolean(paymentStatusData.is_paid),
          status: paymentStatusData.is_paid
            ? 'paid'
            : (paymentStatusData.latest_payment_status || 'none'),
        });
      } else {
        setPaymentStatus({ isPaid: false, status: 'unknown' });
      }

      setLoading(false);
    } catch (err) {
      // セキュリティ: エラーの詳細をログに出力しない
      setError('データの取得に失敗しました。');
      setLoading(false);
    }
  };

  const handleSyncEmailStatus = async () => {
    setSyncEmailStatusError('');
    setSyncingEmailStatus(true);
    try {
      const refreshed = await syncUserInfo();
      if (refreshed) {
        setIsEmailConfirmedInDb(Boolean(refreshed.is_email_confirmed));
        setApplicationStatus({
          ruralChoice: refreshed.rural_preference_status,
          firstChoice: refreshed.first_preference_status,
          secondChoice: refreshed.second_preference_status
        });
        setResults({
          firstResult: results.firstResult,
          secondResult: refreshed.second_result_status,
          finalResult: refreshed.final_result_status
        });
        setExchangeStatus({
          hospital: refreshed.hospital_exchange_preference_status,
          rural: refreshed.rural_exchange_preference_status
        });
      }
    } catch {
      setSyncEmailStatusError('認証状態の同期に失敗しました。');
    } finally {
      setSyncingEmailStatus(false);
    }
  };

  // ステータス表示コンポーネント（色覚特性に配慮し、アイコンとテキストで状態を明示）
  const StatusBadge = ({ status }) => {
    if (status === null || status === undefined || status === '未確定') {
      return (
        <div className="status-badge status-unknown">
          <AlertCircle size={18} strokeWidth={2.5} />
          <span>未確定</span>
        </div>
      );
    } else if (status === 'スキップ') {
      return (
        <div className="status-badge status-skipped">
          <AlertCircle size={18} strokeWidth={2.5} />
          <span>スキップ</span>
        </div>
      );
    } else if (status === true || status === '完了') {
      return (
        <div className="status-badge status-completed">
          <CheckCircle2 size={18} strokeWidth={2.5} />
          <span>完了</span>
        </div>
      );
    } else if (status === '処理中') {
      return (
        <div className="status-badge status-processing">
          <Clock size={18} strokeWidth={2.5} />
          <span>処理中</span>
        </div>
      );
    } else {
      return (
        <div className="status-badge status-incomplete">
          <AlertCircle size={18} strokeWidth={2.5} />
          <span>未完了</span>
        </div>
      );
    }
  };

  const secondChoiceStatus = 'スキップ';


  const formatAnnouncementDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const paymentStatusContent = (() => {
    if (paymentStatus.isPaid || paymentStatus.status === 'unknown') {
      return null;
    }

    if (paymentStatus.status === 'pending') {
      return {
        title: 'お支払いを確認中です',
        body: '決済処理が完了するまでしばらくお待ちください。銀行振込は反映まで時間がかかりますので、しばらくお待ちください。',
      };
    }
    if (paymentStatus.status === 'failed') {
      return {
        title: 'お支払いに失敗しました',
        body: 'お手数ですが、再度お支払いをお試しください。',
      };
    }
    if (paymentStatus.status === 'canceled') {
      return {
        title: 'お支払いがキャンセルされました',
        body: '必要であれば再度お支払いを行えます。',
      };
    }
    if (paymentStatus.status === 'refunded') {
      return {  
        title: 'お支払いが返金されています',
        body: '必要であれば改めてお支払いください。',
      };
    }

    return {
      title: 'お支払いが未完了です',
      body: 'まだ支払いが確認できていません。銀行振込は反映まで時間がかかりますので、しばらくお待ちください。お支払いページからお手続きください。',
    };
  })();

  if (loading) {
    return <LoadingSpinner text="データを読み込んでいます..." />;
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <AlertCircle size={48} />
        <p>{error}</p>
        <button onClick={fetchData} className="btn btn-primary">再試行</button>
      </div>
    );
  }

  // タブ定義
  const tabs = [
    { id: 'overview', label: 'ホーム', icon: LayoutDashboard },
    { id: 'registration', label: '希望登録', icon: FileText },
    { id: 'exchange', label: '交換申請', icon: ArrowLeftRight },
    { id: 'results', label: '結果確認', icon: Trophy },
    { id: 'reports', label: '申し送り', icon: FileEdit },
  ];

  return (
    <div className="dashboard dashboard-container">
      <div className="dashboard-container">
        {!isEmailConfirmedInDb && (
          <div className="email-verify-banner" role="status" aria-live="polite">
            <AlertCircle size={18} strokeWidth={2.5} />
            <div>
              <div className="email-verify-banner-title">メール認証が未完了です</div>
              <div className="email-verify-banner-body">
                受信トレイの確認メールを開き、リンクをクリックして認証を完了させてください。
              </div>
              {syncEmailStatusError && (
                <div className="email-verify-banner-error" role="alert">
                  {syncEmailStatusError}
                </div>
              )}
              <div className="email-verify-banner-actions">
                <Link to="/verify-email" className="email-verify-banner-link">
                  認証画面へ
                </Link>
                <button
                  type="button"
                  className="email-verify-banner-button"
                  onClick={handleSyncEmailStatus}
                  disabled={syncingEmailStatus}
                >
                  {syncingEmailStatus ? '同期中...' : '認証状態を同期'}
                </button>
              </div>
            </div>
          </div>
        )}

        {paymentStatusContent && (
          <div className="email-verify-banner payment-status-banner" role="status" aria-live="polite">
            <CreditCard size={18} strokeWidth={2.5} />
            <div>
              <div className="email-verify-banner-title">{paymentStatusContent.title}</div>
              <div className="email-verify-banner-body">{paymentStatusContent.body}</div>
              <div className="email-verify-banner-actions">
                <Link to="/payment" className="email-verify-banner-link">
                  支払いページへ
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* タブナビゲーション */}
        <nav className="dashboard-nav" aria-label="ダッシュボードメニュー">
          <div className="nav-scroll-container">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  className={`nav-item ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => handleTabChange(tab.id)}
                  aria-current={activeTab === tab.id ? 'page' : undefined}
                >
                  <Icon size={20} strokeWidth={2} />
                  <span>{tab.label}</span>
                  {activeTab === tab.id && (
                    <motion.div
                      className="nav-indicator"
                      layoutId="activeTab"
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </nav>

        {/* メインコンテンツエリア */}
        <main className="dashboard-content">

          {/* オーバービュータブ */}
          {activeTab === 'overview' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="dashboard-grid"
            >
              {/* お知らせセクション */}
              <section className="dashboard-card notification-card" aria-labelledby="notifications-title">
                <div className="card-header">
                  <h2 id="notifications-title" className="card-title">
                    <Megaphone size={24} className="icon-blue" />
                    お知らせ
                  </h2>
                  {notifications.length > 0 && <span className="badge-count">{notifications.length}</span>}
                </div>
                <div className="notification-list">
                  {notifications.length > 0 ? (
                    notifications.map((notification, index) => {
                      const noticeDate = notification.start_at || notification.created_at;
                      const linkLabel = notification.link_label || '詳細を見る';
                      const shouldOpenNewTab =
                        Boolean(notification.link_url && (notification.open_in_new_tab || notification.link_url.startsWith('http')));
                      return (
                        <article
                          key={notification.id || index}
                          className={`notification-item ${notification.is_important ? 'important' : ''}`}
                        >
                          <div className="notification-meta">
                            <time dateTime={noticeDate || ''}>{formatAnnouncementDate(noticeDate)}</time>
                            {notification.is_important && <span className="badge-important">重要</span>}
                          </div>
                          <h3 className="notification-title">{notification.title}</h3>
                          <p className="notification-body">{notification.body}</p>
                          {notification.link_url && (
                            shouldOpenNewTab ? (
                              <a
                                href={notification.link_url}
                                className="text-link"
                                target="_blank"
                                rel="noreferrer"
                              >
                                {linkLabel} <ChevronRight size={16} />
                              </a>
                            ) : (
                              <Link to={notification.link_url} className="text-link">
                                {linkLabel} <ChevronRight size={16} />
                              </Link>
                            )
                          )}
                        </article>
                      );
                    })
                  ) : (
                    <div className="empty-state">
                      <Bell size={40} />
                      <p>現在お知らせはありません</p>
                    </div>
                  )}
                </div>
              </section>

              {/* 今後の日程 */}
              <section className="dashboard-card schedule-card" aria-labelledby="schedule-title">
                <div className="card-header">
                  <h2 id="schedule-title" className="card-title">
                    <Calendar size={24} className="icon-blue" />
                    今後の日程
                  </h2>
                </div>
                <div className="status-list">
                  {schedules.length > 0 ? (
                    schedules.map((schedule) => (
                      <div key={schedule.id} className="status-row">
                        <div className="status-label" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '0.25rem' }}>
                          <span style={{ fontSize: '0.85rem', color: 'var(--primary-color)', fontWeight: 'bold' }}>
                            {schedule.start_date ? new Date(schedule.start_date).toLocaleString('ja-JP', {
                              month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', weekday: 'short'
                            }) : '未設定'}
                            {' － '}
                            {schedule.end_date ? new Date(schedule.end_date).toLocaleString('ja-JP', {
                              month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', weekday: 'short'
                            }) : '未設定'}
                          </span>
                          <span style={{ fontSize: '0.95rem' }}>{schedule.title}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="empty-state" style={{ padding: '1rem' }}>
                      <p style={{ fontSize: '0.9rem' }}>予定なし</p>
                    </div>
                  )}
                </div>
              </section>

              {/* 申請・交換進捗 */}
              <section className="dashboard-card status-card" aria-labelledby="progress-title">
                <div className="card-header">
                  <h2 id="progress-title" className="card-title">
                    <Activity size={24} className="icon-blue" />
                    申請・交換の進捗
                  </h2>
                </div>
                <div className="status-columns">
                  <div className="status-column">
                    <h3 className="status-subtitle">希望登録</h3>
                    <div className="status-list">
                      <div className="status-row">
                        <div className="status-label">
                          <span className="status-number">1</span>
                          <span>地域希望</span>
                        </div>
                        <StatusBadge status={applicationStatus?.ruralChoice} />
                      </div>
                      <div className="status-row">
                        <div className="status-label">
                          <span className="status-number">2</span>
                          <span>1次希望登録</span>
                        </div>
                        <StatusBadge status={applicationStatus?.firstChoice} />
                      </div>
                      <div className="status-row">
                        <div className="status-label">
                          <span className="status-number">3</span>
                          <span>2次希望登録</span>
                        </div>
                        <StatusBadge status={secondChoiceStatus} />
                      </div>
                    </div>
                  </div>
                  <div className="status-column">
                    <h3 className="status-subtitle">交換申請</h3>
                    <div className="status-list">
                      <div className="status-row">
                        <div className="status-label">
                          <span className="status-number">1</span>
                          <span className="status-label-text">
                            <span>地域実習の</span>
                            <span>交換申請</span>
                          </span>
                        </div>
                        <StatusBadge status={exchangeStatus?.rural} />
                      </div>
                      <div className="status-row">
                        <div className="status-label">
                          <span className="status-number">2</span>
                          <span className="status-label-text">
                            <span>2次結果確定後の</span>
                            <span>交換申請</span>
                          </span>
                        </div>
                        <StatusBadge status={exchangeStatus?.hospital} />
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* 抽選結果サマリー */}
              <section className="dashboard-card results-card" aria-labelledby="results-title">
                <div className="card-header">
                  <h2 id="results-title" className="card-title">
                    <Trophy size={24} className="icon-blue" />
                    抽選結果
                  </h2>
                </div>
                <div className="status-list">
                  <div className="status-row">
                    <div className="status-label">
                      <span className="status-number">1</span>
                      <span>1次抽選結果</span>
                    </div>
                    <StatusBadge status={results?.firstResult} />
                  </div>
                  <div className="status-row">
                    <div className="status-label">
                      <span className="status-number">2</span>
                      <span>2次抽選モード</span>
                    </div>
                    <StatusBadge status={results?.secondResult} />
                  </div>
                  <div className="status-row highlight-row">
                    <div className="status-label">
                      <span className="status-icon-check"><Check size={16} /></span>
                      <strong>最終結果</strong>
                    </div>
                    <StatusBadge status={results?.finalResult} />
                  </div>
                </div>
              </section>

            </motion.div>
          )}

          {/* 希望登録タブ */}
          {activeTab === 'registration' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="dashboard-grid"
            >
              <section className="dashboard-card full-width">
                <div className="card-header">
                  <h2 className="card-title">
                    <FileText size={24} className="icon-blue" />
                    希望登録メニュー
                  </h2>
                </div>
                <p className="card-description">
                  各種希望登録はこちらから行えます。締切までに必ず登録を完了させてください。
                </p>
                <div className="action-grid">
                  <Link to="/rural-choice" className="action-card">
                    <div className="action-icon"><MapPin size={32} /></div>
                    <div className="action-content">
                      <h3>地域希望登録</h3>
                      <p>希望する地域を選択します</p>
                    </div>
                    <StatusBadge status={applicationStatus?.ruralChoice} />
                    <ChevronRight className="action-arrow" />
                  </Link>

                  <Link to="/first-choice" className="action-card">
                    <div className="action-icon"><div className="number-icon">1</div></div>
                    <div className="action-content">
                      <h3>1次希望登録</h3>
                      <p>第1希望の病院群を登録します</p>
                    </div>
                    <StatusBadge status={applicationStatus?.firstChoice} />
                    <ChevronRight className="action-arrow" />
                  </Link>

                  <Link to="/second-choice" className="action-card">
                    <div className="action-icon"><div className="number-icon">2</div></div>
                    <div className="action-content">
                      <h3>2次希望登録</h3>
                      <p>第2希望以降を登録します</p>
                    </div>
                    <StatusBadge status={secondChoiceStatus} />
                    <ChevronRight className="action-arrow" />
                  </Link>
                </div>
              </section>

              <section className="dashboard-card full-width">
                <div className="card-header">
                  <h2 className="card-title">
                    <Activity size={24} className="icon-blue" />
                    リアルタイム希望登録状況
                  </h2>
                </div>
                <div className="link-list">
                  <Link to="/application-status" className="list-link-item">
                    <div className="link-icon"><Activity size={20} /></div>
                    <span>リアルタイム希望登録状況</span>
                    <ChevronRight size={16} />
                  </Link>
                </div>
              </section>
            </motion.div>
          )}

          {/* 結果確認タブ */}
          {activeTab === 'results' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="dashboard-grid"
            >
              <section className="dashboard-card full-width">
                <div className="card-header">
                  <h2 className="card-title">
                    <Trophy size={24} className="icon-blue" />
                    結果確認メニュー
                  </h2>
                </div>
                <div className="action-grid">
                  <Link to="/first-result" className="action-card">
                    <div className="action-icon"><div className="number-icon">1</div></div>
                    <div className="action-content">
                      <h3>1次抽選結果詳細</h3>
                      <p>1次抽選の結果を確認します</p>
                    </div>
                    <StatusBadge status={results?.firstResult} />
                    <ChevronRight className="action-arrow" />
                  </Link>

                  <Link to="/second-result" className="action-card">
                    <div className="action-icon"><div className="number-icon">2</div></div>
                    <div className="action-content">
                      <h3>2次抽選結果詳細</h3>
                      <p>2次抽選の結果を確認します</p>
                    </div>
                    <StatusBadge status={results?.secondResult} />
                    <ChevronRight className="action-arrow" />
                  </Link>

                  <Link to="/final-result" className="action-card highlight-card">
                    <div className="action-icon"><Trophy size={32} /></div>
                    <div className="action-content">
                      <h3>最終結果確認</h3>
                      <p>最終的な配属を確認します</p>
                    </div>
                    <StatusBadge status={results?.finalResult} />
                    <ChevronRight className="action-arrow" />
                  </Link>
                </div>
              </section>

              <section className="dashboard-card full-width">
                <div className="card-header">
                  <h2 className="card-title">
                    <Vote size={24} className="icon-blue" />
                    2次抽選モードの投票結果
                  </h2>
                </div>
                <div className="link-list">
                  <Link to="/voting-results" className="list-link-item">
                    <div className="link-icon"><Vote size={20} /></div>
                    <span>2次抽選モードの投票結果</span>
                    <ChevronRight size={16} />
                  </Link>
                </div>
              </section>
            </motion.div>
          )}

          {/* 交換申請タブ */}
          {activeTab === 'exchange' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="dashboard-grid"
            >
              <section className="dashboard-card full-width">
                <div className="card-header">
                  <h2 className="card-title">
                    <Shuffle size={24} className="icon-blue" />
                    地域実習配属交換申請
                  </h2>
                </div>
                <p className="card-description">
                  地域実習配属に関する交換申請はこちらから行います。
                </p>
                <div className="action-grid">
                  <Link to="/rural-exchange" className="action-card">
                    <div className="action-icon"><Shuffle size={32} /></div>
                    <div className="action-content">
                      <h3>地域実習配属交換申請</h3>
                      <p>空き枠・相互交換の希望を提出します</p>
                    </div>
                    <StatusBadge status={exchangeStatus?.rural} />
                    <ChevronRight className="action-arrow" />
                  </Link>

                  <Link to="/rural-exchange-status" className="action-card">
                    <div className="action-icon"><Bell size={32} /></div>
                    <div className="action-content">
                      <h3>地域実習配属交換申請状況</h3>
                      <p>提出済みの申請内容を確認します</p>
                    </div>
                    <ChevronRight className="action-arrow" />
                  </Link>
                </div>
              </section>

              <section className="dashboard-card full-width">
                <div className="card-header">
                  <h2 className="card-title">
                    <ArrowLeftRight size={24} className="icon-blue" />
                    2次結果確定後の交換申請
                  </h2>
                </div>
                <p className="card-description">
                  2次結果確定後の配属交換はこちらから申請します。
                </p>
                <div className="action-grid">
                  <Link to="/exchange" className="action-card">
                    <div className="action-icon"><FileEdit size={32} /></div>
                    <div className="action-content">
                      <h3>2次結果確定後の交換申請</h3>
                      <p>2次結果確定後の配属交換を申請します</p>
                    </div>
                    <StatusBadge status={exchangeStatus?.hospital} />
                    <ChevronRight className="action-arrow" />
                  </Link>

                  <Link to="/exchange-status" className="action-card">
                    <div className="action-icon"><Bell size={32} /></div>
                    <div className="action-content">
                      <h3>2次結果確定後の交換申請状況</h3>
                      <p>申請中のステータスを確認します</p>
                    </div>
                    <ChevronRight className="action-arrow" />
                  </Link>
                </div>
              </section>

              <section className="dashboard-card full-width">
                <div className="card-header">
                  <h2 className="card-title">
                    <TableProperties size={24} className="icon-blue" />
                    学生配属状況一覧
                  </h2>
                </div>
                <div className="action-grid">
                  <Link to="/public-final-assignments" className="action-card">
                    <div className="action-icon"><TableProperties size={32} /></div>
                    <div className="action-content">
                      <h3>配属状況一覧</h3>
                      <p>現状の配属一覧表を確認できます</p>
                    </div>
                    <ChevronRight className="action-arrow" />
                  </Link>

                  <Link to="/application-status" className="action-card">
                    <div className="action-icon"><Activity size={32} /></div>
                    <div className="action-content">
                      <h3>残枠数確認ページ</h3>
                      <p>現在の空き枠状況を確認できます</p>
                    </div>
                    <ChevronRight className="action-arrow" />
                  </Link>
                </div>
              </section>
            </motion.div>
          )}

          {/* 申し送りタブ */}
          {activeTab === 'reports' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="dashboard-grid"
            >
              <section className="dashboard-card full-width">
                <div className="card-header">
                  <h2 className="card-title">
                    <FileEdit size={24} className="icon-blue" />
                    申し送りメニュー
                  </h2>
                </div>
                <p className="card-description">
                  病院からの申し送り内容を確認したり、新しい申し送りを作成したりできます。
                </p>
                <div className="action-grid">
                  <Link to="/hospital-report-list" className="action-card">
                    <div className="action-icon"><FileText size={32} /></div>
                    <div className="action-content">
                      <h3>申し送り一覧</h3>
                      <p>過去の申し送りを確認します</p>
                    </div>
                    <ChevronRight className="action-arrow" />
                  </Link>

                  <Link to="/hospital-report" className="action-card">
                    <div className="action-icon"><FileEdit size={32} /></div>
                    <div className="action-content">
                      <h3>申し送り作成</h3>
                      <p>新しい申し送りを作成します</p>
                    </div>
                    <ChevronRight className="action-arrow" />
                  </Link>

                  <Link to="/placement-status" className="action-card">
                    <div className="action-icon"><CheckCircle2 size={32} /></div>
                    <div className="action-content">
                      <h3>申し送り記載担当確認</h3>
                      <p>担当状況を確認します</p>
                    </div>
                    <ChevronRight className="action-arrow" />
                  </Link>
                </div>
              </section>
            </motion.div>
          )}

        </main>
      </div>
    </div>
  );
}

export default Dashboard;
